import {computed, action, extendObservable} from "mobx";
import axios from "../../../libs/axios_csrf";
import {camelCase, snakeCase} from "@ridi/object-case-converter/dist/index";
import {STATISTICS_PERIODS} from '../constants';
import FeedbackChain from '../models/FeedbackChain'; 
import { default as toCamelCase } from 'lodash/camelCase';

export default class AnalyticsStore {

    constructor(rootStore) {
        this.rootStore = rootStore;
        extendObservable(this, {
            loading: false,
            _newTickets: {
                [STATISTICS_PERIODS.MONTHLY]: {},
                [STATISTICS_PERIODS.ANNUALLY]: {},
            },
            _solvedTickets: {
                [STATISTICS_PERIODS.MONTHLY]: {},
                [STATISTICS_PERIODS.ANNUALLY]: {},
            },
            _totalNewTickets: {
                [STATISTICS_PERIODS.MONTHLY]: 0,
                [STATISTICS_PERIODS.ANNUALLY]: 0,
            },
            _totalSolvedTickets: {
                [STATISTICS_PERIODS.MONTHLY]: 0,
                [STATISTICS_PERIODS.ANNUALLY]: 0,
            },
            _averageComments: {
                [STATISTICS_PERIODS.MONTHLY]: 0,
                [STATISTICS_PERIODS.ANNUALLY]: 0,
            },
            _averageFirstReplyObject: {
                [STATISTICS_PERIODS.MONTHLY]: {},
                [STATISTICS_PERIODS.ANNUALLY]: {},
            },
            _satisfactionRate: {
                [STATISTICS_PERIODS.MONTHLY]: {},
                [STATISTICS_PERIODS.ANNUALLY]: {},
            },
            _priorities: {
                [STATISTICS_PERIODS.MONTHLY]: {},
                [STATISTICS_PERIODS.ANNUALLY]: {},
            },
            period: STATISTICS_PERIODS.MONTHLY,
        });
    };


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // DATA FOR NEW AND SOLVED TICKETS PLOT
    @computed
    get newTickets(){
        return this._newTickets[this.period];
    }

    @computed
    get solvedTickets(){
        return this._solvedTickets[this.period];
    }

    @computed
    get newTicketsKeys(){
        return Object.keys(this.newTickets);
    }

    @computed
    get newTicketsValues(){
        return Object.values(this.newTickets);
    }

    @computed
    get solvedTicketsKeys(){
        return Object.keys(this.solvedTickets);
    }

    @computed
    get solvedTicketsValues(){
        return Object.values(this.solvedTickets);
    }
    // end DATA FOR NEW AND SOLVED TICKETS PLOT
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // GENERAL TICKETS STATISTICS
    @computed
    get totalNewTickets(){
        return this._totalNewTickets[this.period];
    }

    @computed
    get totalSolvedTickets(){
        return this._totalSolvedTickets[this.period];
    }

    @computed
    get totalUnsolvedTickets(){
        return this.totalNewTickets - this.totalSolvedTickets;
    }
    // end GENERAL TICKETS STATISTICS
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // FIRST REPLY STATISTICS
        @computed
    get averageFirstReplyObject(){
        return this._averageFirstReplyObject[this.period];
    }

    @computed
    get averageFirstReply(){
        if( this.averageFirstReplyInDays() ){
            return this.averageFirstReplyObject.days;
        }
        else{
            return this.averageFirstReplyObject.hours;
        }
    }

    averageFirstReplyInDays(){
        return this.averageFirstReplyObject.days != 0;
    }
    // end GENERAL TICKETS STATISTICS
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // DATA FOR SATISFACTION RATE PLOTS
    @computed
    get satisfactionRate(){
        return this._satisfactionRate[this.period];
    }

    @computed
    get satisfactionRateKeys(){
        return Object.keys(this.satisfactionRate).reverse();
    }

    @computed
    get satisfactionRateValues(){
        const total = Object.values(this.satisfactionRate).reduce( (a, b) => a+b, 0 );
        return Object.values(this.satisfactionRate)
                     .map( val => ((val/total)*100).toFixed(1) )
                     .reverse();
    }
    // end DATA FOR SATISFACTION RATE PLOTS
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // DATA FOR PRIORITIES PLOT
    @computed
    get priorities(){
        return this._priorities[this.period];
    }

    @computed
    get prioritiesKeys(){
        return FeedbackChain.priorities.map((p) => toCamelCase(p));
    }

    @computed
    get prioritiesValues(){
        const values  = this.prioritiesKeys.map( (k) => this.priorities[k] );
        return values;
    }
    // end DATA FOR PRIORITIES PLOT
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // DATA FOR COMMENTS
    @computed
    get averageComments(){
        return this._averageComments[this.period];
    }
    // end DATA FOR COMMENTS
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // PERIOD
    @action
    setPeriod(period){
        if( !Object.values(STATISTICS_PERIODS).includes(period) ){
            return null;
        }
        this.period = period;
        this.fetchData();
        return this.period;
    }
    // end PERIOD
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // BACKEND CONNECTIONS
    @action
    fetchData(){
        this.loading = true;
        axios.get('/feedback_chains_statistics.json',{ params: {period: this.period} })
        .then(action((response) => {
            this.loading = false;
            const jsonData = camelCase(response.data, true);
            this._newTickets[this.period] = jsonData.newTickets;
            this._solvedTickets[this.period] = jsonData.solvedTickets;
            this._totalNewTickets[this.period] = jsonData.totalNewTickets;
            this._totalSolvedTickets[this.period] = jsonData.totalSolvedTickets;
            this._averageComments[this.period] = jsonData.averageComments;
            this._averageFirstReplyObject[this.period] = jsonData.averageFirstReply;
            this._satisfactionRate[this.period] = jsonData.satisfactionRate; 
            this._priorities[this.period] = jsonData.priorities;
        })).catch(action((error) => {
            console.log(error);
        }));
    }
    // end BACKEND CONNECTIONS
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

}